import React from 'react';

import ContextSingleton from '../__singletons__/context-singleton';
import { mui } from '../libs';
import { useDispatch, useSelector } from '../libs';
import { BaseStore } from '../types';

/**
 * Snackbar component displays notifications to the user.
 *
 * @requires Redux Store:
 * - state.ui.notification: The current notification object to display.
 *
 * @requires Redux Store Action:
 * - ui.hideNotification: Action to hide the notification.
 *
 * @returns {React.ReactElement} The rendered Snackbar component.
 */

const Snackbar = () => {
  const actions = ContextSingleton.getInstance().actions;

  const dispatch = useDispatch();
  const notification = useSelector((state: BaseStore) => state.ui.notification);
  const [isClosing, setIsClosing] = React.useState(false);

  const hideNotification = () => dispatch(actions.ui.hideNotification());

  React.useEffect(() => {
    if (notification) setIsClosing(false);
  }, [notification]);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setIsClosing(true);
  };

  const handleExited = () => {
    setIsClosing(false);
    hideNotification();
  };

  return (
    <mui.core.Snackbar
      open={!!notification && !isClosing}
      autoHideDuration={5_000}
      onClose={handleClose}
      TransitionProps={{ onExited: handleExited }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <mui.core.Alert
        severity={notification?.severity}
        onClose={handleClose}
        variant="filled"
        sx={{
          color: 'white',
          '& .MuiAlert-action .MuiIconButton-root': {
            color: 'white',
          },
        }}
      >
        {notification?.message}
      </mui.core.Alert>
    </mui.core.Snackbar>
  );
};

export default Snackbar;
